export const RUDDERSTACK_EVENTS = Object.freeze({
    submit: (form: string): string => `Submitted ${form}`,
    click: (element: string): string => `Clicked ${element}`,
    trigger: (activity: string): string => `Triggered ${activity}`,
    custom: (customEvent: string): string => customEvent,
});

export const EVENT_TYPES = Object.freeze({
    FTD: 'FTD',
    ENTRY: 'ENTRY',
    KYC: 'KYC',
    RG: 'RG',
    REGISTRATION: 'REGISTRATION',
});

export enum AnalyticsEvent {
    //general
    TAB = 'tab',
    CLICK = 'click',
    VIEW = 'view',
    EXPAND_PLAYER_PROPS = 'expand_player_props',
    ADD_1ST_PICK = 'add_1st_pick',
    INPUT_ENTRY_AMOUNT = 'input_entry_amount',
    ALWAYS_ACCEPT_PROJECTION_CHANGES = 'always_accept_projection_changes',
    ACCEPT_PROJECTION_CHANGES = 'accept_projection_changes',
    REJECT_PROJECTION_CHANGES = 'reject_projection_changes',
    NEW_PICK = 'new_pick',
    REMOVE_PICK = 'remove_pick',
    REUSE_LINEUP = 'reuse_lineup',
    REUSE_LINEUP_ENTRY_SCREEN = 'reuse_lineup_view_entry_screen',
    P2P_MAX_ENTRY_EXCEEDED = 'p2p_max_entry_exceeded',
    //errors
    PRE_SUBMIT_ERRORS = 'pre_submit_errors',
    //restricted combos
    RESTRICTED_COMBINATION = 'restricted_combination',
    RESTRICTED_COMBINATION_KEEP_PICK = 'restricted_combination_keep_pick',
    RESTRICTED_COMBINATION_REPLACE_PICK = 'restricted_combo_replace_pick',
    // share entry
    SHARE_ENTRY = 'share_entry',
    SHARE_OPTION = 'share_option',
    //prebuilt lineups
    VIEW_FEATURED_LINEUPS = 'view_featured_lineups',
    ADD_PREBUILT_LINEUP = 'add_prebuilt_lineup',
    SHOW_REPLACE_LINEUP_MODAL = 'show_replace_lineup_modal',
    REPLACE_LINEUP_BUTTON = 'replace_lineup_button',
    KEEP_LINEUP_BUTTON = 'keep_lineup_button',
    // deposit
    WALLET_DEPOSIT = 'wallet_depositCTA',
    DEPOSIT_QUICKBUTTON = 'deposit_amt_quickbutton',
    DEPOSIT_ABORT = 'deposit_amt_abort',
    DEPOSIT_SUBMIT = 'deposit_amt_submit',
    DEPOSIT_SUBMIT_EDIT = 'deposit_amt_edit_submit',
    DEPOSIT_METHOD_ABORT = 'deposit_method_abort',
    DEPOSIT_METHOD_ADD_DEBIT = 'deposit_method_add_debit',
    DEPOSIT_METHOD_ADD_IBT = 'deposit_method_add_IBT',
    DEPOSIT_METHOD_VENMO = 'deposit_method_venmo',
    DEPOSIT_DEBITJS_ABORT = 'deposit_debitJS_abort',
    DEPOSIT_METHOD_PAYPAL = 'deposit_method_paypal',
    DEPOSIT_METHOD_SAVED_DEBIT = 'deposit_method_saved_debit',
    DEPOSIT_METHOD_SAVED_IBT = 'deposit_method_saved_IBT',
    DEPOSIT_METHOD_FANTASY_MONEY = 'deposit_method_fantasy_money',
    DEPOSIT_METHOD_SOCIAL_WALLET = 'deposit_method_social_wallet',
    FTD_ATTEMPT_SUCCESS = 'ftd_attempt_success',
    DEPOSIT_SUCCESS = 'deposit_success',
    DEPOSIT_FAILURE_SUPPORT_REQUEST = 'deposit_failure_supportrequest',
    DEPOSIT_FAILURE_RETRY = 'deposit_failure_retry',
    DEPOSIT_FAILURE_ABORT = 'deposit_failure_abort',
    DEPOSIT_SALVAGE_DISMISS = 'deposit_salvage_dismiss',
    DEPOSIT_SALVAGE_ABORT = 'deposit_salvage_abort',
    DEPOSIT_SALVAGE = 'deposit_salvage',
    DEPOSIT_DEBITJS_SUBMIT = 'deposit_debitJS_submit',
    // create entry
    PLACE_ENTRY = 'place_entry',
    PLACE_ENTRY_P2P = 'place_entry_p2p',
    PICKS_ENTRY_SUCCESS = 'picks_entry_success',
    PICKS_ENTRY_ERROR = 'picks_entry_error',

    // search players
    SEARCH_OPEN = 'search_open',
    SEARCH_LEAGUE_SELECTED = 'search_league_selected',
    SEARCH_PLAYER_SELECTED = 'search_player_selected',

    // authentication
    CREATION_EMAIL_ABORTED = 'creation_email_aborted',
    CREATION_PHONE_NUMBER_ABORTED = 'creation_phone_number_aborted',
    CREATION_MFA_ABORTED = 'creation_mfa_aborted',
    LOGIN_ABORTED = 'login_aborted',
    LOGIN_MFA_ABORTED = 'login_mfa_aborted',
    LOGIN_SUSPENDED_ABORTED = 'login_suspended_aborted',
    LOGIN_SUSPENDED_CONTACT_SUPPORT = 'login_suspended_contact_support',
    LOGIN_SUSPENDED_DISMISS = 'login_suspended_dismiss',
    // kyc
    FIRST_ATTEMPT_ABORT = 'first_attempt_abort',
    SECOND_ATTEMPT_ABORT = 'second_attempt_abort',
    SSN4_SUBMIT = 'ssn4_submit',
    SSN9_SUBMIT = 'ssn9_submit',
    TCS_ABORT = 'tcs_abort',
    TCS_SUBMIT = 'tcs_submit',
    ATTEMPT_FAILED_ABORT = 'attempt_failed_abort',
    ATTEMPT_FAILED_RETRY = 'attempt_failed_retry',
    IDPV_ABORT = 'idpv_abort',
    IDPV_LAUNCH = 'idpv_launch',
    IDPV_CANCEL_REQUEST = 'idpv_cancel_request',
    IDPV_CANCEL_CONFIRM = 'idpv_cancel_confirm',
    IDPV_CANCEL_ABORTED = 'idpv_cancel_aborted',
    IDPV_FAILED_DISMISS = 'idpv_failed_dismiss',
    REVIEW_ABORT = 'review_abort',
    REVIEW_EDIT = 'review_edit',
    KYC_SUCCESS = 'kyc_success',
    KYC_FAILURE = 'kyc_failure',
    FTD_SKIP = 'ftd_skip',
    FTD_SUBMIT = 'ftd_submit',
    // KYC Trustly
    KYC_SELECTOR_ACCESSED = 'kyc_selector_accessed',
    KYC_SELECTED_TRUSTLY = 'kyc_selected_trustly',
    KYC_SELECTED_IDCOMPLY = 'kyc_selected_idcomply',
    KYC_TRUSTLY_TCS_ABORT = 'kyc_trustly_tcs_abort',
    KYC_TRUSTLY_TCS_SUCCESS = 'kyc_trustly_tcs_success',
    KYC_TRUSTLY_DEPOSIT_ABORT = 'kyc_trustly_deposit_abort',
    KYC_TRUSTLY_DEPOSIT_SUBMIT = 'kyc_trustly_deposit_submit',
    KYC_TRUSTLY_DEPOSIT_AMOUNT_CHANGED = 'kyc_trustly_deposit_changed',
    KYC_TRUSTLY_FAILED = 'kyc_trustly_failed',
    KYC_TRUSTLY_SUCCESS = 'kyc_trustly_success',
    WITHDRAW_VERIFICATION_SELECTOR_ACCESSED = 'withdraw_verification_selector_accessed',
    WITHDRAW_VERIFICATION_SELECTED_TRUSTLY = 'withdraw_verification_selected_trustly',
    WITHDRAW_VERIFICATION_SELECTED_IDPV = 'withdraw_verification_selected_idpv',
    WITHDRAW_VERIFICATION_BLOCKED = 'withdraw_verification_blocked',
    WITHDRAW_VERIFICATION_NO_PAYMENT_METHOD = 'withdraw_verification_no_payment_method',
    WITHDRAW_VERIFICATION_IDPV_RETRY = 'withdraw_verification_idpv_retry',
    // Push Primer
    PUSH_PRIMER_SKIP = 'push_primer_skip',
    PUSH_PRIMER_SETTINGS = 'push_primer_settings',
    PUSH_PRIMER_NOTIFICATION_ALLOW = 'push_primer_notification_allow',
    // SBK Place Bet
    VIEW_LOBBY = 'view_lobby',
    CLICK_LEAGUE = 'click_league',
    CLICK_EVENT = 'click_event',
    ADD_SELECTION = 'add_selection',
    CLICK_VIEW_BETS = 'click_view_bets',
    CLICK_PLACE_BET = 'click_place_bet',
    VIEW_BET_CONFIRMATION = 'view_bet_confirmation',
    ADD_PREBUILT_BETS = 'add_prebuilt_bets',

    // SBK Bet Slip
    CLICK_BETSLIP_CLOSE_BUTTON = 'click_betslip_close_button',
    TOGGLE_BET_TYPE_TAB = 'toggle_bet_type_tab',
    TOGGLE_SELECTION = 'toggle_selection',
    CLICK_BALANCE_DROPDOWN = 'click_balance_dropdown',
    CLICK_BET_SUMMARY = 'click_bet_summary',
    CLICK_WAGER_INPUT = 'click_wager_input',
    CLICK_PAYOUT_SECTION = 'click_payout_section',
    TOGGLE_CURRENCY = 'toggle_currency',
    CLEAR_BET_SLIP = 'clear_bet_slip',
    DELETE_INDIVIDUAL_SELECTION = 'delete_individual_selection',
    REVIEW_MULTIPLIER_CHANGES = 'review_multiplier_changes',
    BETSLIP_INTERACTION_SUMMARY = 'betslip_interaction_summary',

    // account
    DETAILS_EDIT_EMAIL = 'details_edit_email',
    DETAILS_EDIT_PHONE = 'details_edit_phone',
    DETAILS_EDIT_PASSWORD = 'details_edit_password',
    DETAILS_EDIT_BIOMETRICS_TURNON = 'details_edit_biometrics_turnon',
    DETAILS_EDIT_BIOMETRICS_TURNOFF = 'details_edit_biometrics_turnoff',
    DETAILS_CLOSEACCT = 'details_closeacct',
    DETAILS_CLOSEACCT_CONFIRM = 'details_closeacct_confirm',
    DETAILS_CLOSEACCT_CANCEL = 'details_closeacct_cancel',
    STATE_CHANGE = 'state_change',
    ACCOUNT_SETTINGS = 'account_settings',
    WALLET_WITHDRAW = 'wallet_withdrawCTA',
    WITHDRAWAL_UNAVAILABLE_CLOSE = 'withdrawal_unavailable_close',
    WITHDRAW_NO_PAYMENT_METHOD = 'withdraw_no_payment_method',
    WITHDRAW_SUBMIT = 'withdraw_submit',
    WITHDRAW_CHANGE_METHOD = 'withdraw_change_method',
    SELECT_WITHDRAW_METHOD = 'select_withdraw_method',
    WITHDRAW_VIEW_TRANSACTION_HISTORY = 'withdraw_view_txn_history',
    WITHDRAW_SUCCESS_DONE = 'withdraw_success_done',
    WITHDRAW_FAIL_LOBBY = 'withdraw_fail_lobby',
    WITHDRAW_FAIL_RETRY = 'withdraw_fail_retry',
    CONTACT_SUPPORT = 'contact_support',
    WITHDRAW_SUCCESS_ABORT = 'withdraw_success_abort',

    //gaming limits
    DEPOSIT_LIMITS = 'deposit_limit',
    PLAY_LIMITS = 'play_limit',
    MAX_SINGLE_PLAY_LIMITS = 'single_max',
    LIMITS_HISTORY = 'limits_history',
    STATE_LIMITS = 'state_limits',
    REALITY_CHECK = 'reality_check',
    HIDE_VERTICALS = 'hide_verticals',
    TIMEOUT_LIMITS = 'timeout',
    SELF_EXCLUSION = 'exclusion',
    ADD_DEPOSIT_LIMIT = 'add_deposit_limit',
    CONFIRM_ADD_DEPOSIT_LIMIT = 'confirm_add_deposit_limit',
    EDIT_DEPOSIT_LIMIT = 'edit_deposit_limit',
    REMOVE_DEPOSIT_LIMIT = 'remove_deposit_limit',
    UPDATE_DEPOSIT_LIMIT = 'update_deposit_limit',
    CONFIRM_REMOVE_DEPOSIT_LIMIT = 'confirm_remove_deposit_limit',
    CONFIRM_UPDATE_DEPOSIT_LIMIT = 'confirm_update_deposit_limit',
    CANCEL_REMOVE_DEPOSIT_LIMIT = 'cancel_remove_deposit_limit',
    CANCEL_UPDATE_DEPOSIT_LIMIT = 'cancel_update_deposit_limit',
    RESPONSIBLE_GAMING = 'responsible_gaming_resources',
    ADD_PLAY_LIMIT = 'add_play_limit',
    EDIT_PLAY_LIMIT = 'edit_play_limit',
    CONFIRM_ADD_PLAY_LIMIT = 'confirm_add_play_limit',
    CONFIRM_UPDATE_PLAY_LIMIT = 'confirm_update_play_limit',
    CONFIRM_REMOVE_PLAY_LIMIT = 'confirm_remove_play_limit',
    UPDATE_PLAY_LIMIT = 'update_play_limit',
    REMOVE_PLAY_LIMIT = 'remove_play_limit',
    CANCEL_UPDATE_PLAY_LIMIT = 'cancel_update_play_limit',
    CANCEL_REMOVE_PLAY_LIMIT = 'cancel_remove_play_limit',
    ADD_MAX_SINGLE_PLAY_LIMIT = 'add_max_single_play_limit',
    EDIT_MAX_SINGLE_PLAY_LIMIT = 'edit_max_single_play_limit',
    CONFIRM_ADD_MAX_SINGLE_PLAY_LIMIT = 'add_max_single_play_limit',
    CONFIRM_UPDATE_MAX_SINGLE_PLAY_LIMIT = 'update_max_single_play_limit',
    CONFIRM_REMOVE_MAX_SINGLE_PLAY_LIMIT = 'remove_max_single_play_limit',
    UPDATE_MAX_SINGLE_PLAY_LIMIT = 'update_max_single_play_limit',
    CANCEL_MAX_SINGLE_PLAY_LIMIT = 'cancel_max_single_play_limit',
    ACCEPT_PAYMENT_LIMIT = 'accept_payment_limit',
    CANCEL_PAYMENT_LIMIT = 'cancel_payment_limit',
    ADD_TIMEOUT = 'add_timeout',
    SET_TIMEOUT_PERIOD = 'set_timeout_period',
    CONFIRM_ADD_TIMEOUT = 'confirm_add_timeout',
    CANCEL_ADD_TIMEOUT = 'cancel_add_timeout',
    GAMING_LIMITS_ABORT = 'gaming_limits_abort',
    LIMITS_ABORT = 'limits_abort',
    AMOUNT_ABORT = 'amount_abort',
    ADD_SELF_EXCLUSION = 'add_self_exclusion',
    CONFIRM_ADD_SELF_EXCLUSION = 'confirm_add_self_exclusion',
    CANCEL_SELF_EXCLUSION = 'cancel_self_exclusion',

    //top 10
    POPULAR_PLAYERS_OPEN_CARD = 'top_10_players_open_card',

    //scoreboard
    SCOREBOARD_EXPAND = 'scoreboard_expand',
    SCOREBOARD_COLLAPSE = 'scoreboard_collapse',

    //cancel entry
    CANCEL_ENTRY = 'cancel_entry',
    CANCEL_ENTRY_YES = 'cancel_entry_yes',
    CANCEL_ENTRY_NO = 'cancel_entry_no',
    CONFIRM_CANCEL_ENTRY_TIMEOUT = 'confirm_cancel_entry_timeout',

    //view event
    VIEW_FULL_EVENT = 'view_full_event',

    // cash out
    CASH_OUT_START = 'cash_out_start',
    CASH_OUT_CANCEL = 'cash_out_cancel',
    CASH_OUT_CONFIRM = 'cash_out_confirm',
    CASH_OUT_SUCCESS = 'cash_out_success',
    CASH_OUT_ERROR = 'cash_out_error',
    CASH_OUT_PRICE_CHANGED = 'cash_out_price_changed',

    //edge combo
    ALWAYS_MAKE_EDGE_COMBO = 'always_make_edge_combo',
    ALWAYS_MAKE_EDGE_COMBO_OFF = 'always_make_edge_combo_off',
    ADD_EDGE_COMBO = 'add_edge_combo',

    // fantasy circular pager
    FANTASY_PAGER_PLAYER_SWIPE = 'fantasy_pager_player_swipe',
    FANTASY_PAGER_PLAYER_CLICK = 'fantasy_pager_player_click',
    FANTASY_PAGER_EVENT_SWIPE = 'fantasy_pager_event_swipe',
    FANTASY_PAGER_EVENT_CLICK = 'fantasy_pager_event_click',

    FANTASY_EVENT_TAB_PROJECTIONS = 'fantasy_event_tab_projections',
    FANTASY_EVENT_TAB_LINEUPS = 'fantasy_event_tab_lineups',
    FANTASY_EVENT_TAB_PLAYERS = 'fantasy_event_tab_players',
    FANTASY_EVENT_TAB_PREVIEW = 'fantasy_event_tab_preview',

    FANTASY_PLAYER_TAB_LINEUPS = 'fantasy_player_tab_lineups',
    FANTASY_PLAYER_TAB_INSIGHTS = 'fantasy_player_tab_insights',

    //AI match preview
    FANTASY_MATCH_PREVIEW_SHOWN = 'fantasy_match_preview_shown',
    FANTASY_MATCH_PREVIEW_ERROR = 'fantasy_match_preview_error',

    //Player insights
    FANTASY_PLAYER_INSIGHTS_SHOWN = 'fantasy_player_insights_shown',
    FANTASY_PLAYER_INSIGHTS_ERROR = 'fantasy_player_insights_error',

    // SBK circular pager
    SBK_PAGER_EVENT_SWIPE = 'sbk_pager_event_swipe',

    // Cross-sell claim funnel (Picks -> Sweeps)
    CLAIM_FUNNEL_STEP = 'claim_funnel_step',

    // Promos
    OPT_IN_CAROUSEL = 'opt_in_carousel',
    OPT_IN_PROMOS_SCREEN = 'opt_in_promos_screen',
    OPT_IN_PROMO_DETAILS = 'opt_in_promo_details',
    OPEN_PROMO_DETAILS_FROM_LOBBY = 'open_promo_details_from_lobby',
    OPEN_PROMO_DETAILS_FROM_PROMOHUB = 'open_promo_details_from_promohub',
    VIEW_ALL_PROMOS_FROM_LOBBY_CAROUSEL = 'view_all_promos_from_lobby_carousel',
    VIEW_ALL_PROMOS_FROM_LOBBY_TILE = 'view_all_promos_from_lobby_tile',
    EXPAND_AVAILABLE_PROMOS_FROM_CHECKOUT = 'expand_available_promos_from_checkout',
    COLLAPSE_AVAILABLE_PROMOS_FROM_CHECKOUT = 'collapse_available_promos_from_checkout',
    APPLY_PROMO_FROM_CHECKOUT = 'apply_promo_from_checkout',
    REMOVE_PROMO_FROM_CHECKOUT = 'remove_promo_from_checkout',
    PROMO_REMOVED_MODAL_SHOWN = 'promo_removed_modal_shown',
    KEEP_PROMO_FROM_REMOVED_MODAL = 'keep_promo_from_removed_modal',
    ADD_PICK_FROM_REMOVED_MODAL = 'add_pick_from_removed_modal',
    DISMISS_PROMO_REMOVED_MODAL = 'dismiss_promo_removed_modal',
    BETR_BUCKS_SHEET_TURN_ON_BETR_BUCKS = 'betr_bucks_sheet_turn_on_betr_bucks',
    BETR_BUCKS_SHEET_KEEP_APPLIED_PROMO = 'betr_bucks_sheet_keep_applied_promo',
}

export enum AnalyticsUserProperty {
    CUSTOM_USER_ID = 'custom_user_id',
    CUSTOM_USER_GLOBAL_ID = 'custom_user_global_id',
}

export const LOG_TAG = '[RudderStack]';
